import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function InscriptionAdminPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const navigate = useNavigate();

  /* =========================
     📥 Charger les inscriptions
  ========================== */
  const loadMembers = async () => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("membreToken");

      if (!token) {
        navigate("/login");
        return;
      }

      const res = await fetch("/api/admin/members", {
        headers: {
          Authorization: "Bearer " + token,
        },
      });

      if (res.status === 403) {
        localStorage.removeItem("membreToken");
        navigate("/login");
        return;
      }

      if (!res.ok) throw new Error("Erreur API members");

      const data = await res.json();
      if (!data.success) throw new Error(data.message);

      setMembers(data.members || []);
    } catch (err) {
      console.error("❌ loadMembers:", err);
      setError("Impossible de charger les inscriptions.");
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
  }, []);

  /* =========================
     ✅ ACCEPTER
  ========================== */
  const approveMember = async (member_id) => {
    if (!window.confirm("Accepter ce membre ?")) return;

    const token = localStorage.getItem("membreToken");

    await fetch("/api/admin/approve", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify({ member_id }),
    });

    loadMembers();
  };

  /* =========================
     ❌ REFUSER
  ========================== */
  const rejectMember = async (member_id) => {
    if (!window.confirm("Refuser ce membre ?")) return;

    const token = localStorage.getItem("membreToken");

    await fetch("/api/admin/members", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify({ member_id }),
    });

    loadMembers();
  };

  /* =========================
     🎯 FILTRAGE
  ========================== */
  const filteredMembers = members.filter((m) => {
    const matchStatus =
      statusFilter === "all" || m.status === statusFilter;

    const matchSearch =
      `${m.prenom} ${m.nom} ${m.email}`
        .toLowerCase()
        .includes(search.toLowerCase());

    return matchStatus && matchSearch;
  });

  /* =========================
     UI
  ========================== */
  if (loading) return <p className="p-4">⏳ Chargement…</p>;

  if (error) {
    return (
      <div className="p-4">
        <p className="text-red-600">❌ {error}</p>
        <button onClick={loadMembers}>🔄 Réessayer</button>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">
        📋 Inscriptions – Administration
      </h1>

      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <input
          className="border px-3 py-2 rounded w-full"
          placeholder="🔎 Rechercher (nom ou email)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="border px-3 py-2 rounded"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Tous</option>
          <option value="pending">⏳ En attente</option>
          <option value="approved">✅ Acceptés</option>
          <option value="rejected">❌ Refusés</option>
        </select>
      </div>

      
      <div className="md:hidden space-y-3">
        {filteredMembers.map((m) => (
          <div
            key={m.member_id}
            className="border rounded-lg p-3 shadow-sm"
          >
            <p className="font-semibold">
              {m.prenom} {m.nom}
            </p>
            <p className="text-sm break-all">{m.email}</p>
            <p className="mt-1">
              {m.status === "pending" && "⏳ En attente"}
              {m.status === "approved" && "✅ Accepté"}
              {m.status === "rejected" && "❌ Refusé"}
            </p>

            {m.status === "pending" && (
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => approveMember(m.member_id)}
                  className="flex-1 bg-green-600 text-white py-1 rounded"
                >
                  Accepter
                </button>
                <button
                  onClick={() => rejectMember(m.member_id)}
                  className="flex-1 bg-red-600 text-white py-1 rounded"
                >
                  Refuser
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full border mt-4 text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="border p-2">Nom</th>
              <th className="border p-2">Email</th>
              <th className="border p-2">Statut</th>
              <th className="border p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMembers.map((m) => (
              <tr key={m.member_id}>
                <td className="border p-2">
                  {m.prenom} {m.nom}
                </td>
                <td className="border p-2">{m.email}</td>
                <td className="border p-2">
                  {m.status === "pending" && "⏳ En attente"}
                  {m.status === "approved" && "✅ Accepté"}
                  {m.status === "rejected" && "❌ Refusé"}
                </td>
                <td className="border p-2 text-center">
                  {m.status === "pending" ? (
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => approveMember(m.member_id)}
                        className="bg-green-600 text-white px-3 py-1 rounded"
                      >
                        Accepter
                      </button>
                      <button
                        onClick={() => rejectMember(m.member_id)}
                        className="bg-red-600 text-white px-3 py-1 rounded"
                      >
                        Refuser
                      </button>
                    </div>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredMembers.length === 0 && (
        <p className="mt-4 text-gray-600">Aucun résultat.</p>
      )}
    </div>
  );
}
